import { useState, useCallback, useRef, useEffect, useContext } from 'react'
import useOffer from 'hooks/Offers/useOffer'
import { isBedbankOffer, isLEHotel } from 'lib/offer/offerTypes'
import offerPageURL from 'lib/offer/offerPageURL'
import GeoContext from 'contexts/geoContext'
import { buildOfferContext } from 'tripPlanner/utils/offerContext'
import { escapeContextLabel } from 'tripPlanner/utils/contextTag'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'

// Package type for offer context (covers both LE Hotel and Bedbank packages)
interface OfferPackage {
  name?: string
  roomType?: { name?: string }
  price?: number
  sell?: { price?: number; currency?: string }
}

type UseMessageContextArgs = {
  streamingSubmit: (message: string) => Promise<void>
  setFollowTail: (follow: boolean) => void
  setMessageInputValue: (value: string) => void
  focusChatInput?: () => void
}

export function useMessageContext({
  streamingSubmit,
  setFollowTail,
  setMessageInputValue,
  focusChatInput,
}: UseMessageContextArgs) {
  const { currentCurrency } = useContext(GeoContext)

  const [activeMultiChoiceSelection, setActiveMultiChoiceSelection] = useState<Array<string>>([])
  const [mobileSelectionText, setMobileSelectionText] = useState<string | null>(null)
  const [offerContextTag, setOfferContextTag] = useState<{ label: string; offerId: string } | null>(null)

  const pendingHotelOfferIdsRef = useRef<string | null>(null)

  // Fetch offer data for context tag
  const [contextOfferData] = useOffer<App.AnyOffer>(offerContextTag?.offerId || '')

  // Update context tag label when offer data loads
  useEffect(() => {
    if (contextOfferData && offerContextTag) {
      const name = isBedbankOffer(contextOfferData)
        ? contextOfferData.property?.name
        : isLEHotel(contextOfferData)
          ? contextOfferData.property?.name || contextOfferData.name
          : contextOfferData.name

      if (name && offerContextTag.label !== name) {
        setOfferContextTag({ label: name, offerId: offerContextTag.offerId })
      }
    }
  }, [contextOfferData, offerContextTag])

  const handleSubmit = useCallback(async(message: string, options?: SendMessageOptions) => {
    setMessageInputValue('')
    setFollowTail(false)

    // Combine multi-choice selection with user's typed message
    const selectionPart = activeMultiChoiceSelection.length > 0
      ? activeMultiChoiceSelection.join(', ')
      : ''
    let combinedMessage = selectionPart && message.trim()
      ? `${selectionPart}, ${message}`
      : selectionPart || message

    // Append hotel information if available (carousel hotel details for the AI)
    // Wrapped in <Context> so it is stripped from the user-visible bubble and
    // does not interfere with pill-selection matching on historical messages.
    if (pendingHotelOfferIdsRef.current) {
      combinedMessage = `${combinedMessage}\n\n<Context label="Hotel search results">${pendingHotelOfferIdsRef.current}</Context>`
      pendingHotelOfferIdsRef.current = null
    }

    // Append LE offer ID when adding an offer to the trip
    // (used by map explore, hotel listing drawer, and carousel "Add to trip" flows)
    if (options?.offerId) {
      const offerTypeLine = options.offerType ? `\nOffer Type: ${options.offerType}` : ''
      const contextLabel = options.offerType === 'experience' ? 'LE Experience offer' : 'LE Hotel offer'
      combinedMessage = `${combinedMessage}\n<Context label="${contextLabel}">Offer ID: ${options.offerId}${offerTypeLine}</Context>`
    }

    // Append mobile selection context if available
    if (mobileSelectionText) {
      combinedMessage = `${combinedMessage}\n<Context label="${escapeContextLabel(mobileSelectionText)}">The user highlighted the following text: ${mobileSelectionText}</Context>`
    }

    // Append offer context if context tag is present
    if (offerContextTag && contextOfferData) {
      // Build offer context using helper function
      const propertyName = isBedbankOffer(contextOfferData)
        ? contextOfferData.property.name
        : isLEHotel(contextOfferData)
          ? contextOfferData.property?.name || contextOfferData.name
          : contextOfferData.name

      const description = isBedbankOffer(contextOfferData)
        ? (('description' in contextOfferData ? contextOfferData.description : '') || contextOfferData.tagline || '')
        : isLEHotel(contextOfferData)
          ? contextOfferData.description || ''
          : ''

      // Get room packages data (simplified for context)
      const roomPackages: Array<{ name: string; price?: number; currencyCode?: string }> = []
      if ('packages' in contextOfferData && contextOfferData.packages) {
        (contextOfferData.packages as Array<OfferPackage>).slice(0, 3).forEach((pkg) => {
          if (isBedbankOffer(contextOfferData)) {
            roomPackages.push({
              name: pkg.name || 'Room Package',
              price: pkg.sell?.price,
              currencyCode: pkg.sell?.currency,
            })
          } else {
            roomPackages.push({
              name: pkg.roomType?.name || pkg.name || 'Room Package',
              price: pkg.price,
              currencyCode: currentCurrency,
            })
          }
        })
      }

      const finePrint = isLEHotel(contextOfferData)
        ? contextOfferData.finePrint
        : isBedbankOffer(contextOfferData)
          ? contextOfferData.property?.finePrintOverride
          : undefined

      const offerUrl = offerPageURL(contextOfferData)

      const offerContextString = buildOfferContext(
        contextOfferData,
        propertyName,
        description,
        roomPackages,
        finePrint,
        offerUrl,
        currentCurrency,
      )

      combinedMessage = `${combinedMessage}\n\n<Context label="${escapeContextLabel(propertyName)}">${offerContextString}</Context>`
      // Clear context tag after sending
      setOfferContextTag(null)
    }

    // Clear the selection after submission
    setActiveMultiChoiceSelection([])
    setMobileSelectionText(null)

    return streamingSubmit(combinedMessage)
  }, [
    streamingSubmit,
    setFollowTail,
    setMessageInputValue,
    activeMultiChoiceSelection,
    mobileSelectionText,
    offerContextTag,
    contextOfferData,
    currentCurrency,
  ])

  const handleSelectionSubmit = useCallback((message: string, selectedText: string) => {
    // Format the message with context tags for the LLM
    // The <Context> tags are stripped in non-dev mode UI but sent to the LLM
    const messageWithContext = `${message}\n<Context label="${escapeContextLabel(selectedText)}">The user highlighted the following text: ${selectedText}</Context>`
    return handleSubmit(messageWithContext)
  }, [handleSubmit])

  const handleHotelOffersDisplayed = useCallback((hotelInfo: string) => {
    // Store the formatted hotel information to be included in the next main-chat message.
    // Each carousel overwrites the previous; the next user message consumes and clears it.
    pendingHotelOfferIdsRef.current = hotelInfo
  }, [])

  const handleMultiChoiceSelectionChange = useCallback((selection: Array<string>) => {
    setActiveMultiChoiceSelection(selection)
    focusChatInput?.()
  }, [focusChatInput])

  const handleRemoveTag = useCallback((tag: string) => {
    setActiveMultiChoiceSelection(prev => prev.filter(s => s !== tag))
    // Also clear mobile selection if it matches the removed tag
    if (mobileSelectionText && tag === mobileSelectionText) {
      setMobileSelectionText(null)
    }
    // Also clear offer context tag if it matches
    if (tag === offerContextTag?.label) {
      setOfferContextTag(null)
    }
  }, [mobileSelectionText, offerContextTag])

  const handleMobileTextSelection = useCallback((selectedText: string) => {
    setMobileSelectionText(selectedText)
  }, [])

  const resetSelectionState = useCallback(() => {
    setActiveMultiChoiceSelection([])
    setMobileSelectionText(null)
    setOfferContextTag(null)
    pendingHotelOfferIdsRef.current = null
  }, [])

  return {
    activeMultiChoiceSelection,
    mobileSelectionText,
    offerContextTag,
    handleSubmit,
    handleSelectionSubmit,
    handleHotelOffersDisplayed,
    handleMultiChoiceSelectionChange,
    handleRemoveTag,
    handleMobileTextSelection,
    pendingHotelOfferIdsRef,
    setOfferContextTag,
    resetSelectionState,
  }
}
